import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, TrashIcon, DocumentIcon, EyeIcon, EnvelopeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Propuesta } from '../types';
import proposalsService from '../services/proposals.service';
import contractsService from '../services/contracts.service';

const PropuestaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [propuesta, setPropuesta] = useState<Propuesta | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [plantillas, setPlantillas] = useState<any[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('profesional');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertData, setConvertData] = useState({
    numeroContrato: '',
    fechaInicio: '',
    fechaFin: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        console.log('Fetching propuesta with ID:', id);
        const [propuestaData, plantillasData] = await Promise.all([
          proposalsService.getPropuestaById(parseInt(id)),
          proposalsService.getPlantillas()
        ]);
        console.log('Propuesta data received:', propuestaData);
        setPropuesta(propuestaData);
        setPlantillas(plantillasData);
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar la propuesta');
        navigate('/propuestas');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!propuesta || !window.confirm('¿Estás seguro de que deseas eliminar esta propuesta?')) {
      return;
    }

    try {
      await proposalsService.deletePropuesta(propuesta.id);
      navigate('/propuestas');
    } catch (error) {
      console.error('Error deleting propuesta:', error);
      alert('Error al eliminar la propuesta');
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return 'bg-gray-100 text-gray-800';
      case 'enviada':
        return 'bg-blue-100 text-blue-800';
      case 'aceptada':
        return 'bg-green-100 text-green-800';
      case 'rechazada':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isVencida = propuesta?.validezHasta && new Date(propuesta.validezHasta) < new Date();

  const handleSendEmail = async () => {
    if (!propuesta || !propuesta.cliente?.correo) {
      alert('El cliente no tiene correo registrado');
      return;
    }
    const email = prompt('Correo del destinatario:', propuesta.cliente.correo);
    if (!email) return;

    setSendingEmail(true);
    try {
      await proposalsService.enviarPropuesta(propuesta.id, { to: [email] });
      alert('Propuesta enviada exitosamente');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error al enviar la propuesta por correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleGeneratePdf = async (plantilla?: string) => {
    if (!propuesta) return;
    setGeneratingPdf(true);
    setShowTemplateModal(false);
    try {
      // Generar PDF en memoria y abrirlo directamente
      const pdfUrl = await proposalsService.generarPdfPreview(propuesta.id, plantilla || selectedTemplate);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error al generar el PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleConvertToContract = async () => {
    if (!propuesta) return;
    
    if (!convertData.numeroContrato || !convertData.fechaInicio || !convertData.fechaFin) {
      alert('Por favor completa todos los campos');
      return;
    }

    try {
      const contrato = await contractsService.convertirPropuestaAContrato(propuesta.id, convertData);
      alert('Contrato creado exitosamente');
      setShowConvertModal(false);
      navigate(`/contratos/${contrato.id}`);
    } catch (error: any) {
      console.error('Error converting to contract:', error);
      alert(error.response?.data?.message || 'Error al convertir a contrato');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!propuesta) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Propuesta no encontrada</h3>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/propuestas')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a propuestas
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{propuesta.titulo}</h1>
            <p className="mt-2 text-sm text-gray-700">{propuesta.cliente?.nombre}</p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/propuestas/${propuesta.id}/editar`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Editar
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Eliminar
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información Principal */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Detalles de la Propuesta</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Descripción</dt>
                <dd className="mt-1 text-sm text-gray-900">{propuesta.descripcion}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Valor Total</dt>
                <dd className="mt-1 text-lg font-semibold text-green-600">
                  {formatCurrency(Number(propuesta.totalNeto ?? propuesta.valorPropuesto ?? 0))}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Válido hasta</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {propuesta.validezHasta ? new Date(propuesta.validezHasta).toLocaleDateString('es-CO') : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Estado</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoColor(propuesta.estado)}`}>
                    {propuesta.estado}
                  </span>
                  {isVencida && (
                    <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      Vencida
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fecha de Envío</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {propuesta.fechaEnvio ? new Date(propuesta.fechaEnvio).toLocaleDateString('es-CO') : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Creada por</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {propuesta.comercial?.email || 'Sistema'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fecha de Creación</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(propuesta.createdAt).toLocaleDateString('es-CO')}
                </dd>
              </div>
            </div>
            
            {propuesta.observaciones && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500">Observaciones</dt>
                <dd className="mt-2 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                  {propuesta.observaciones}
                </dd>
              </div>
            )}
          </div>

          {/* Cliente Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Información del Cliente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Comercial</dt>
                <dd className="mt-1 text-sm text-gray-900">{propuesta.comercial?.nombre || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">NIT</dt>
                <dd className="mt-1 text-sm text-gray-900">{propuesta.cliente?.nit}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Tipo de Entidad</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">{propuesta.cliente?.tipoEntidad}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Teléfono</dt>
                <dd className="mt-1 text-sm text-gray-900">{propuesta.cliente?.telefono || 'No especificado'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{propuesta.cliente?.correo || 'No especificado'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Ubicación</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {propuesta.cliente?.municipio?.nombre}, {propuesta.cliente?.municipio?.departamento?.nombre}
                </dd>
              </div>
            </div>
          </div>

          {/* Detalles de la propuesta */}
          {propuesta.detalles && propuesta.detalles.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Detalles de Servicios</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Desc. %</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {propuesta.detalles.map((detalle, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-gray-900">{detalle.descripcion}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{detalle.cantidad}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(Number(detalle.precioUnitario))}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{detalle.descuentoPct ? `${Number(detalle.descuentoPct)}%` : '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(Number(detalle.subtotalNeto))}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={4} className="px-4 py-3 text-sm text-gray-900 text-right">Total Bruto:</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(Number(propuesta.totalBruto ?? 0))}</td>
                    </tr>
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={4} className="px-4 py-3 text-sm text-gray-900 text-right">Descuento Total:</td>
                      <td className="px-4 py-3 text-sm text-red-600 text-right">-{formatCurrency(Number(propuesta.totalDescuento ?? 0))}</td>
                    </tr>
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan={4} className="px-4 py-3 text-base text-gray-900 text-right">Total Neto:</td>
                      <td className="px-4 py-3 text-base text-green-600 text-right">{formatCurrency(Number(propuesta.totalNeto ?? 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Archivos */}
          {propuesta.archivos && propuesta.archivos.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Archivos Adjuntos</h2>
              <div className="space-y-3">
                {propuesta.archivos?.map((archivo, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900">{archivo.filename}</span>
                    </div>
                    <a
                      href={archivo.s3Url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </a>
                  </div>
                )) || []}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Resumen</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Valor Total</dt>
                <dd className="mt-1 text-2xl font-semibold text-green-600">
                  {formatCurrency(Number(propuesta.totalNeto ?? propuesta.valorPropuesto ?? 0))}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Estado Actual</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getEstadoColor(propuesta.estado)}`}>
                    {propuesta.estado}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones</h3>
            <div className="space-y-3">
              <button
                onClick={() => setShowTemplateModal(true)}
                disabled={generatingPdf}
                className="flex items-center justify-center w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                {generatingPdf ? 'Generando...' : 'Generar PDF'}
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex items-center justify-center w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                {sendingEmail ? 'Enviando...' : 'Enviar por Email'}
              </button>
              <button
                onClick={() => setShowConvertModal(true)}
                className="flex items-center justify-center w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
              >
                <DocumentIcon className="h-4 w-4 mr-2" />
                Convertir a Contrato
              </button>
              <Link
                to={`/clientes/${propuesta.clienteId}`}
                className="block w-full text-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Ver Cliente
              </Link>
              {propuesta.estado === 'aceptada' && (
                <Link
                  to={`/contratos/nuevo?clienteId=${propuesta.clienteId}`}
                  className="block w-full text-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Crear Contrato
                </Link>
              )}
            </div>
          </div>

          {/* File Statistics */}
          {propuesta.archivos && propuesta.archivos.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Archivos</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total de archivos</dt>
                  <dd className="mt-1 text-2xl font-semibold text-blue-600">
                    {propuesta.archivos.length}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Selecciona una plantilla</h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {plantillas.map((plantilla: any) => (
                  <button
                    key={plantilla.slug}
                    onClick={() => setSelectedTemplate(plantilla.slug)}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      selectedTemplate === plantilla.slug
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900 mb-1">{plantilla.nombre}</div>
                    <div className="text-sm text-gray-600">{plantilla.descripcion}</div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleGeneratePdf()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Generar PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Contract Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Convertir a Contrato</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Contrato *
                  </label>
                  <input
                    type="text"
                    value={convertData.numeroContrato}
                    onChange={(e) => setConvertData({...convertData, numeroContrato: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="CON-2025-0001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Inicio *
                  </label>
                  <input
                    type="date"
                    value={convertData.fechaInicio}
                    onChange={(e) => setConvertData({...convertData, fechaInicio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Fin *
                  </label>
                  <input
                    type="date"
                    value={convertData.fechaFin}
                    onChange={(e) => setConvertData({...convertData, fechaFin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowConvertModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConvertToContract}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                >
                  Crear Contrato
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropuestaDetail;